import { Injectable } from '@angular/core';
import { Message } from '../models/message';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, interval, Observable, switchMap, tap } from 'rxjs';
import { MessageService } from './message.service';
import { MatterService } from './matter.service';
import { PagedList } from '../models/paged-list';
import { Router } from '@angular/router';
import { PagingOptions } from '../models/paged-options';

@Injectable({
  providedIn: 'root'
})
export class MessageListService {

 private inboxMessagesSubject = new BehaviorSubject<PagedList<Message>>({
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    items: []
  });

  private sentMessagesSubject = new BehaviorSubject<PagedList<Message>>({
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    items: []
  });

  private searchResultSubject = new BehaviorSubject<PagedList<Message>>({
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    items: []
  });


  private unreadMessagesCountSubject = new BehaviorSubject<number>(0);
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private searchTermSubject = new BehaviorSubject<string>('');
  private filtersSubject = new BehaviorSubject<{ searchFilter: string; searchTarget: string }>({
    searchFilter: '',
    searchTarget: 'subjectBody',
  });
  private pagingRequestSubject = new BehaviorSubject<PagingOptions>({ pageSize: 10, pageNumber: 1 });


  searchTerm$ = this.searchTermSubject.asObservable();
  filters$ = this.filtersSubject.asObservable();
  pagingRequest$ = this.pagingRequestSubject.asObservable();

    set searchTerm(term: string) {
    this.searchTermSubject.next(term);
  }

  set filters(filters: { searchFilter: string; searchTarget: string }) {
    this.filtersSubject.next(filters);
  }

   set pagingRequest(pagingRequest: PagingOptions) {
    this.pagingRequestSubject.next(pagingRequest);
  }


  inboxMessages$: Observable<PagedList<Message>> = this.inboxMessagesSubject.asObservable();
  sentMessages$: Observable<PagedList<Message>> = this.sentMessagesSubject.asObservable();
  unreadMessageCount$: Observable<number> = this.unreadMessagesCountSubject.asObservable();
  searchResult$: Observable<PagedList<Message>> = this.searchResultSubject.asObservable();
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  inboxPageNumber = 1;
  inboxPageSize = 5;
  sentPageNumber = 1;
  sentPageSize =5;
  matterId: string | undefined;

  private inboxMessagesRetrieved = false;
  private sentMessagesRetrieved = false;
  private unreadMessagesRetrieved = false;

  constructor(
    private messageService: MessageService,
    private matterService: MatterService,
    private router: Router
  ) {

   interval(300000).subscribe(() => {
      // don't auto refresh if we are on the search page
      if (!this.router.url.includes('/search')) {
        this.refreshInboxMessages(this.matterId, this.inboxPageNumber, this.inboxPageSize);
        this.refreshSentMessages(this.matterId, this.sentPageNumber, this.sentPageSize);
        this.refreshUnreadMessages();
      }
    });

    // react to changes in the search term, filters or paging
    combineLatest([
      this.searchTerm$.pipe(debounceTime(300), distinctUntilChanged()),
      this.filters$,
      this.pagingRequest$,
    ])
      .pipe(
        tap(() => this.loadingSubject.next(true)), // Set loading state
        switchMap(([searchTerm, filters, pagingRequest]) => {
          return this.search(
            searchTerm,
            filters.searchFilter,
            filters.searchTarget,
            pagingRequest
          );
        }),
        tap(() => this.loadingSubject.next(false)) // Clear loading state
      )
      .subscribe({
        next: (pagedResults: PagedList<Message>) => {
          this.searchResultSubject.next(pagedResults); // Update search result
        },
        error: (err) => {
          this.loadingSubject.next(false);
          console.error('Error during search:', err);
        },
      });

  }
 
  getInboxMessages(matterId?: string, pageNumber: number = 1, pageSize: number = 5): void {
    if (this.inboxMessagesRetrieved) return;

    this.inboxPageNumber = pageNumber;
    this.inboxPageSize = pageSize;
    this.matterId = matterId;
    const inboxObservable = matterId
      ? this.matterService.getMessages(matterId, false, { pageNumber, pageSize })
      : this.messageService.getMessages(false, { pageNumber, pageSize });

    this.loadingSubject.next(true);

    inboxObservable.pipe(
      tap(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (pagedMessages: PagedList<Message>) => {
        this.inboxMessagesSubject.next(pagedMessages);
        this.inboxMessagesRetrieved = true;
      },
      error: (err) => {
        this.loadingSubject.next(false);
        console.error('Error refreshing inbox messages:', err);
      },
    });
  }

    getUnreadMessages(): void {
    if(this.unreadMessagesRetrieved) return;
    
    this.messageService.getUnreadCount().subscribe((count: number) => {
      this.unreadMessagesCountSubject.next(count);
      this.unreadMessagesRetrieved = true;
    });
  }

    getSentMessages(matterId?: string, pageNumber: number = 1, pageSize: number = 5): void {
    if (this.sentMessagesRetrieved) return;

    this.sentPageNumber = pageNumber;
    this.sentPageSize = pageSize;
    this.matterId = matterId;
    const sentObservable = matterId
      ? this.matterService.getMessages(matterId, true, { pageNumber, pageSize })
      : this.messageService.getMessages(true, { pageNumber, pageSize });

    this.loadingSubject.next(true);

    sentObservable.pipe(
      tap(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (pagedMessages: PagedList<Message>) => {
        this.sentMessagesSubject.next(pagedMessages);
        this.sentMessagesRetrieved = true;
      },
      error: (err) => {
        this.loadingSubject.next(false);
        console.error('Error refreshing sent messages:', err);
      },
    });
  }

  search(searchTerm: string, searchFilter: string, searchTarget: string, pagingRequest: PagingOptions) : Observable<PagedList<Message>> {
       

    let hasAttachments: boolean | undefined = undefined;
    if (searchFilter === 'hasAttachment') {
      hasAttachments = true;
    }

    let isUnread: boolean | undefined = undefined;
    if (searchFilter === 'unread') {
      isUnread = true;
    }

    let hasUnreadAttachments: boolean | undefined = undefined;
    if (searchFilter === 'uneradAttachments') {
      hasUnreadAttachments = true;
    }

    const searchSubject = searchTarget === 'subject' || searchTarget === 'subjectBody';
    const searchBody = searchTarget === 'body' || searchTarget === 'subjectBody';

    return this.messageService.search(searchTerm ?? '', hasAttachments, isUnread, hasUnreadAttachments, searchSubject, searchBody, pagingRequest, true);
    
  }

  refreshUnreadMessages(){
     this.unreadMessagesRetrieved = false;
     this.getUnreadMessages();
  }

  refreshInboxMessages(matterId?: string, pageNumber: number = 1, pageSize: number = 5): void {
    this.inboxPageNumber = pageNumber;
    this.inboxPageSize = pageSize;
    this.matterId = matterId;
    this.inboxMessagesRetrieved = false; // Reset flag
    this.getInboxMessages(matterId, pageNumber, pageSize);
   
  }

  refreshSentMessages(matterId?: string, pageNumber: number = 1, pageSize: number = 5): void {
    this.sentPageNumber = pageNumber;
    this.sentPageSize = pageSize;
    this.matterId = matterId;
    this.sentMessagesRetrieved = false; // Reset flag
    this.getSentMessages(matterId, pageNumber, pageSize);
  }
}
